/**
 * Helpers for the search results list: sorting, distances and prices.
 */

export const SORT_RECOMMENDED = 'recommended';
export const SORT_PRICE = 'price';
export const SORT_DISTANCE = 'distance';

const EARTH_RADIUS_KM = 6371;

function toRadians(degrees) {
  return (degrees * Math.PI) / 180;
}

/**
 * Great-circle distance in kilometres
 *
 * @param {object} from - `{ lat, lng }`
 * @param {Array} to - `[lat, lng]`, as offers store their location
 * @returns {number|null} Distance, `null` when either point is missing
 */
export function distanceKm(from, to) {
  if (
    !from ||
    !Number.isFinite(from.lat) ||
    !Number.isFinite(from.lng) ||
    !Array.isArray(to) ||
    to.length !== 2 ||
    !Number.isFinite(to[0]) ||
    !Number.isFinite(to[1])
  ) {
    return null;
  }

  const dLat = toRadians(to[0] - from.lat);
  const dLng = toRadians(to[1] - from.lng);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(from.lat)) *
      Math.cos(toRadians(to[0])) *
      Math.sin(dLng / 2) ** 2;

  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Distance rounded for display: one decimal under 10 km, whole km above
 */
export function formatDistance(km) {
  return km < 10 ? km.toFixed(1) : String(Math.round(km));
}

/**
 * Sort a copy of the results.
 *
 * - `recommended`: the order the API returns ("yes" hosts first, then recently active)
 * - `price`: cheapest per night first, hosts without a price last
 * - `distance`: closest to `center` first (needs a `center`, else unchanged)
 */
export function sortResults(offers, sort, center) {
  const sorted = [...offers];

  if (sort === SORT_PRICE) {
    return sorted.sort((a, b) => {
      const priceA = a.nightlyFrom ?? Infinity;
      const priceB = b.nightlyFrom ?? Infinity;
      return priceA - priceB;
    });
  }

  if (sort === SORT_DISTANCE && center) {
    return sorted.sort((a, b) => {
      const distanceA = distanceKm(center, a.location) ?? Infinity;
      const distanceB = distanceKm(center, b.location) ?? Infinity;
      return distanceA - distanceB;
    });
  }

  return sorted;
}

/**
 * Money for display, e.g. "€30" or "€12.50", in the offer's currency
 */
export function formatPrice(amount, currency = 'EUR') {
  const hasCents = Math.round(amount * 100) % 100 !== 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0,
    }).format(amount);
  } catch {
    // Unknown currency code
    return `${currency} ${hasCents ? amount.toFixed(2) : Math.round(amount)}`;
  }
}
